// Modal "📅 Linha do tempo das vendas" do Relatório de Vendas (pedido do
// Vagner, 22/10/2026). Abre pelo cartão "vs mesmo período" ou pelo botão do
// gráfico mês a mês. Duas visões:
// - um mês: dia a dia, corrida do acumulado contra o mês anterior (a conta do
//   cartão), período do mês (início/meio/fim) e a lista dos dias de venda;
// - todos os meses: mapa de calor mês × dia, período do mês com mais vendas,
//   dia da semana e os dias campeões.
// O mês em andamento aparece nos gráficos, mas fica FORA das porcentagens da
// visão geral (os dias que ainda não chegaram distorceriam o resultado).
// "Vendas" = pedidos (itens do mesmo pedido têm o mesmo grupo_parcelas).
import React, { useState, useEffect, useMemo } from 'react';
import { hojeBrasilia } from '../comparativoPeriodo';

const MESES_AB = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
const MESES_NOME = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
const SEM = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb']; // índice = getDay()
const SEM_NOME = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const ORDEM_SEMANA = [1, 2, 3, 4, 5, 6, 0]; // seg → dom
const PERIODOS = [
  { nome: 'início', rot: 'Início', faixa: '1 a 10' },
  { nome: 'meio', rot: 'Meio', faixa: '11 a 20' },
  { nome: 'fim', rot: 'Fim', faixa: '21 a 31' },
];
const p2 = (n) => String(n).padStart(2, '0');
const ultimoDia = (ano, mes) => new Date(ano, mes, 0).getDate(); // mes de 1 a 12
const diaSemana = (ano, mes, dia) => new Date(ano, mes - 1, dia).getDay();
const periodoDe = (dia) => (dia <= 10 ? 0 : dia <= 20 ? 1 : 2);
const mesAnterior = (ano, mes) => (mes === 1 ? [ano - 1, 12] : [ano, mes - 1]);
const chaveDe = (ano, mes) => `${ano}-${p2(mes)}`;
const maiuscula = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const secao = { background: '#fff', border: '1px solid #c8d0da', borderRadius: 8, padding: '12px 14px' };
const tituloSec = { fontSize: 13, fontWeight: 800, color: '#0a1628', marginBottom: 8 };
const nota = { fontSize: 10.5, color: '#6b7280', fontWeight: 400 };

export default function LinhaTempoVendas({ vendas, ano, mesInicial, privado, escopo, onFechar }) {
  const brl = (n) => (privado ? 'R$ •••' : `R$ ${(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  const mil = (n) => (privado ? '•••' : n >= 1000 ? `${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} mil` : String(Math.round(n)));
  const qtd = (n, um = 'venda', varios = 'vendas') => (privado ? '•••' : `${n} ${n === 1 ? um : varios}`);

  const [hA, hM, hD] = hojeBrasilia().split('-').map(Number);
  const chaveHoje = chaveDe(hA, hM);
  const anoNum = +ano;

  // porDia['AAAA-MM'][dia] = { valor, pedidos:Set } — todos os anos, para a
  // corrida de janeiro poder comparar com dezembro do ano anterior
  const porDia = useMemo(() => {
    const m = {};
    for (const v of vendas) {
      const d = String(v.data_venda || '').slice(0, 10);
      if (d.length < 10) continue;
      const mes = (m[d.slice(0, 7)] = m[d.slice(0, 7)] || {});
      const x = (mes[+d.slice(8, 10)] = mes[+d.slice(8, 10)] || { valor: 0, pedidos: new Set() });
      x.valor += Number(v.valor) || 0;
      x.pedidos.add(v.grupo_parcelas || v.id);
    }
    return m;
  }, [vendas]);

  // Meses do ano com venda (os botões) e a faixa do mapa (do 1º ao último, ou até o mês corrente)
  const mesesComVenda = useMemo(() => Array.from({ length: 12 }, (_, k) => k + 1)
    .filter(mm => Object.values(porDia[chaveDe(anoNum, mm)] || {}).some(x => x.valor > 0)), [porDia, anoNum]);

  const [sel, setSel] = useState(() => {
    const ini = +mesInicial || (anoNum === hA ? hM : 0);
    if (ini && mesesComVenda.includes(ini)) return ini;
    return mesesComVenda.length ? mesesComVenda[mesesComVenda.length - 1] : 'todos';
  });
  const [dica, setDica] = useState(null); // dia em foco no gráfico dia a dia

  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onFechar]);

  const botoes = [{ k: 'todos', rot: 'Todos os meses' }, ...mesesComVenda.map(mm => ({ k: mm, rot: MESES_AB[mm - 1] }))];

  return (
    <div onClick={onFechar} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400, padding: 12 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#f1f5f9', borderRadius: 10, width: '96vw', maxWidth: 1100, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' }}>
        <div style={{ background: '#0a1628', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ color: '#fff', fontSize: 14, fontWeight: 800 }}>
            📅 Linha do tempo das vendas <span style={{ color: '#94a3b8', fontSize: 11.5, fontWeight: 500, marginLeft: 6 }}>{ano} · {escopo}</span>
          </span>
          <button onClick={onFechar} aria-label="Fechar" style={{ background: 'none', border: 'none', color: '#94a3b8', fontSize: 20, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {botoes.map(b => {
              const on = sel === b.k;
              return (
                <button key={b.k} onClick={() => { setSel(b.k); setDica(null); }} style={{
                  padding: '5px 14px', fontSize: 12, fontWeight: 700, borderRadius: 6, cursor: 'pointer',
                  border: '1px solid ' + (on ? '#0a1628' : '#c8d0da'), background: on ? '#0a1628' : '#fff', color: on ? '#fff' : '#374151',
                }}>{b.rot}</button>
              );
            })}
            <span style={{ marginLeft: 'auto', ...nota }}>segue os filtros de consultor e serviço do relatório</span>
          </div>
          {!mesesComVenda.length ? (
            <div style={{ ...secao, textAlign: 'center', color: '#6b7280', fontSize: 13 }}>Nenhuma venda em {ano} com os filtros atuais.</div>
          ) : sel === 'todos' ? (
            <VisaoTodos {...{ porDia, anoNum, mesesComVenda, chaveHoje, hA, hM, hD, brl, mil, qtd, setSel }} />
          ) : (
            <VisaoMes {...{ porDia, anoNum, mm: sel, chaveHoje, hD, brl, mil, qtd, dica, setDica }} />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Um mês ──────────────────────────────────────────────────────────────────
function VisaoMes({ porDia, anoNum, mm, chaveHoje, hD, brl, mil, qtd, dica, setDica }) {
  const chave = chaveDe(anoNum, mm);
  const dias = porDia[chave] || {};
  const nDias = ultimoDia(anoNum, mm);
  const aberto = chave === chaveHoje;
  const ate = aberto ? hD : nDias;
  const [aAnt, mAnt] = mesAnterior(anoNum, mm);
  const diasAnt = porDia[chaveDe(aAnt, mAnt)] || {};
  const nDiasAnt = ultimoDia(aAnt, mAnt);
  const limiteAnt = aberto ? Math.min(hD, nDiasAnt) : nDiasAnt;

  const valorDe = (d) => dias[d]?.valor || 0;
  const total = Object.values(dias).reduce((s, x) => s + x.valor, 0);
  const pedidosMes = new Set(Object.values(dias).flatMap(x => [...x.pedidos])).size;
  const diasComVenda = Object.keys(dias).filter(d => dias[d].valor > 0).length;
  const melhor = Object.keys(dias).map(Number).reduce((b, d) => (b == null || valorDe(d) > valorDe(b) ? d : b), null);
  const antAte = Object.entries(diasAnt).filter(([d]) => +d <= limiteAnt).reduce((s, [, x]) => s + x.valor, 0);
  const delta = antAte > 0 ? (total - antAte) / antAte : null;
  const ab = MESES_AB[mm - 1].toLowerCase();
  const abAnt = MESES_AB[mAnt - 1].toLowerCase();

  const kpis = [
    { r: aberto ? `Vendido 1–${hD}/${ab}` : `Vendido em ${MESES_NOME[mm - 1]}`, v: brl(total), d: qtd(pedidosMes) },
    { r: 'Dias com venda', v: `${diasComVenda} de ${ate}`, d: `${ate - diasComVenda} dia${ate - diasComVenda === 1 ? '' : 's'} sem venda${aberto ? ' até hoje' : ''}` },
    melhor != null && { r: '🏆 Melhor dia', v: `${p2(melhor)}/${p2(mm)} · ${SEM[diaSemana(anoNum, mm, melhor)]}`, d: `${brl(valorDe(melhor))} · ${qtd(dias[melhor].pedidos.size)}` },
    {
      r: aberto ? `vs 1–${limiteAnt}/${abAnt}` : `vs ${MESES_NOME[mAnt - 1]}`,
      v: delta == null ? '—' : `${delta >= 0 ? '↑' : '↓'} ${Math.abs(Math.round(delta * 100))}%`,
      cor: delta == null ? '#9ca3af' : delta >= 0 ? '#15803d' : '#dc2626',
      d: antAte > 0 ? `${abAnt}${aberto ? ' no mesmo período' : ' inteiro'}: ${brl(antAte)}` : `${abAnt} sem venda no sistema`,
    },
  ].filter(Boolean);

  // Período do mês
  const per = PERIODOS.map(() => ({ valor: 0, pedidos: new Set() }));
  Object.entries(dias).forEach(([d, x]) => { const p = per[periodoDe(+d)]; p.valor += x.valor; x.pedidos.forEach(id => p.pedidos.add(id)); });
  const periodoHoje = aberto ? periodoDe(hD) : 3;

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 8 }}>
        {kpis.map(k => (
          <div key={k.r} style={{ ...secao, padding: '9px 13px' }}>
            <div style={{ fontSize: 10.5, color: '#6b7280', fontWeight: 700 }}>{k.r}</div>
            <div style={{ fontSize: 19, fontWeight: 800, color: k.cor || '#0a1628', fontVariantNumeric: 'tabular-nums' }}>{k.v}</div>
            <div style={{ fontSize: 10.5, color: '#6b7280' }}>{k.d}</div>
          </div>
        ))}
      </div>

      <div style={secao}>
        <div style={tituloSec}>📈 Dia a dia — {MESES_NOME[mm - 1]}/{anoNum} <span style={nota}>· barra = valor vendido no dia · fim de semana em cinza claro · passe o mouse no dia</span></div>
        <GraficoDiaADia {...{ dias, anoNum, mm, nDias, aberto, hD, melhor, brl, mil, qtd, dica, setDica }} />
      </div>

      <div style={secao}>
        <div style={tituloSec}>🏁 Corrida do acumulado — {MESES_NOME[mm - 1]} × {MESES_NOME[mAnt - 1]}, dia a dia <span style={nota}>{aberto && '· é a conta do cartão "vs mesmo período" '}· passe o mouse para ver o valor de cada dia</span></div>
        <GraficoCorrida {...{ dias, diasAnt, ate, nDias, nDiasAnt, aberto, hD, ab, abAnt, anoNum, mm, aAnt, mAnt, brl, mil, qtd }} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
        <div style={secao}>
          <div style={tituloSec}>🗓️ Período do mês — {MESES_NOME[mm - 1]}</div>
          {PERIODOS.map((p, i) => {
            const pct = total ? per[i].valor / total : 0;
            const situacao = i === periodoHoje ? `em andamento (até o dia ${hD})` : i > periodoHoje ? 'ainda não começou' : '';
            return (
              <div key={p.rot} style={{ marginBottom: 9 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, gap: 8 }}>
                  <b style={{ color: '#0a1628' }}>{p.rot} · dias {p.faixa}</b>
                  <span style={{ fontVariantNumeric: 'tabular-nums' }}>{brl(per[i].valor)} · {qtd(per[i].pedidos.size)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ height: 8, borderRadius: 3, background: '#1d4ed8', width: `${Math.max(pct * 60, per[i].valor ? 1 : 0)}%` }} />
                  <span style={{ fontSize: 12, fontWeight: 800, color: '#0a1628' }}>{Math.round(pct * 100)}%</span>
                  {situacao && <span style={nota}>{situacao}</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div style={secao}>
          <div style={tituloSec}>📋 Dias de venda — {MESES_NOME[mm - 1]}</div>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11.5, fontVariantNumeric: 'tabular-nums' }}>
            <tbody>
              {Object.keys(dias).map(Number).sort((a, b) => a - b).map(d => (
                <tr key={d} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '3px 6px' }}>{p2(d)}/{p2(mm)}</td>
                  <td style={{ padding: '3px 6px', color: '#6b7280' }}>{SEM[diaSemana(anoNum, mm, d)]}</td>
                  <td style={{ padding: '3px 6px' }}>{qtd(dias[d].pedidos.size)}</td>
                  <td style={{ padding: '3px 6px', width: '35%' }}>
                    <div style={{ height: 8, borderRadius: 3, background: d === melhor ? '#0a1628' : '#1d4ed8', width: `${valorDe(d) / (valorDe(melhor) || 1) * 100}%` }} />
                  </td>
                  <td style={{ padding: '3px 6px', textAlign: 'right', fontWeight: 700 }}>{brl(valorDe(d))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function GraficoDiaADia({ dias, anoNum, mm, nDias, aberto, hD, melhor, brl, mil, qtd, dica, setDica }) {
  const W = 1000, H = 210, x0 = 44, topo = 18, base = 168;
  const col = (W - x0 - 10) / nDias;
  const vmax = Math.max(1, ...Object.values(dias).map(x => x.valor));
  const altura = (v) => v / vmax * (base - topo - 16);
  const passo = vmax > 20000 ? 10000 : vmax > 8000 ? 5000 : vmax > 4000 ? 2500 : 1000;
  const linhas = [];
  for (let t = 0; t <= vmax; t += passo) linhas.push(t);
  const xHoje = x0 + hD * col;
  return (
    <div style={{ position: 'relative' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }} onMouseLeave={() => setDica(null)}>
        <defs>
          <pattern id="lt-futuro" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <line x1="0" y1="0" x2="0" y2="6" stroke="#e5e7eb" strokeWidth="2" />
          </pattern>
        </defs>
        {Array.from({ length: nDias }, (_, i) => i + 1).map(d => {
          const x = x0 + (d - 1) * col;
          const fds = [0, 6].includes(diaSemana(anoNum, mm, d));
          return (
            <g key={'f' + d}>
              {fds && <rect x={x} y={topo} width={col} height={base - topo} fill="#f8fafc" />}
              {aberto && d > hD && <rect x={x} y={topo} width={col} height={base - topo} fill="url(#lt-futuro)" />}
            </g>
          );
        })}
        {linhas.map(t => {
          const y = base - altura(t);
          return (
            <g key={'g' + t}>
              <line x1={x0} x2={W - 10} y1={y} y2={y} stroke="#eef2f7" />
              <text x={x0 - 6} y={y + 3} fontSize="9" fill="#9ca3af" textAnchor="end">{t ? mil(t) : '0'}</text>
            </g>
          );
        })}
        {Array.from({ length: nDias }, (_, i) => i + 1).map(d => {
          const x = x0 + (d - 1) * col, cx = x + col / 2;
          const v = dias[d]?.valor || 0, h = altura(v);
          const ehHoje = aberto && d === hD;
          return (
            <g key={'d' + d}>
              {v > 0 ? (
                <>
                  <rect x={x + col * 0.18} y={base - h} width={col * 0.64} height={h} rx="2" fill={d === melhor ? '#0a1628' : '#1d4ed8'} />
                  <text x={cx} y={base - h - 4} fontSize="9" fill="#374151" textAnchor="middle">{mil(v)}</text>
                </>
              ) : (!aberto || d <= hD) && <rect x={x + col * 0.3} y={base - 1.5} width={col * 0.4} height="1.5" fill="#cbd5e1" />}
              <text x={cx} y={base + 13} fontSize="9.5" fill={ehHoje ? '#0a1628' : '#6b7280'} fontWeight={ehHoje ? 700 : 400} textAnchor="middle">{d}</text>
              <text x={cx} y={base + 24} fontSize="8" fill="#9ca3af" textAnchor="middle">{SEM[diaSemana(anoNum, mm, d)]}</text>
              <rect x={x} y={topo} width={col} height={base - topo + 26} fill="transparent" style={{ cursor: v ? 'pointer' : 'default' }}
                onMouseEnter={() => setDica(v ? { d, x: cx, y: base - h } : null)} onClick={() => setDica(v ? { d, x: cx, y: base - h } : null)} />
            </g>
          );
        })}
        {aberto && hD < nDias && (
          <>
            <line x1={xHoje} x2={xHoje} y1={topo} y2={base} stroke="#dc2626" strokeDasharray="3 3" />
            <text x={xHoje + 4} y={topo + 10} fontSize="9.5" fill="#dc2626" fontWeight="700">hoje</text>
            {nDias - hD >= 4 && <text x={x0 + (hD + (nDias - hD) / 2) * col} y={(topo + base) / 2} fontSize="10" fill="#94a3b8" textAnchor="middle">ainda não aconteceu</text>}
          </>
        )}
      </svg>
      {dica && dias[dica.d] && (
        <div style={{
          position: 'absolute', left: `${Math.min(dica.x / W * 100, 78)}%`, top: `${Math.max(0, (dica.y - 52) / H * 100)}%`,
          transform: 'translateX(12px)', background: '#0a1628', color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 11.5, pointerEvents: 'none', whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        }}>
          <b>{SEM[diaSemana(anoNum, mm, dica.d)]}, {p2(dica.d)}/{p2(mm)} · {brl(dias[dica.d].valor)}</b>
          <div style={{ color: '#cbd5e1', fontSize: 10.5 }}>{qtd(dias[dica.d].pedidos.size)}</div>
        </div>
      )}
    </div>
  );
}

// Dica da corrida (passar o mouse): a linha-guia e os pontos deslizam de um dia
// para o outro, a caixa entra com fade e os números trocam com um leve sobe.
// Quem pediu "menos movimento" no sistema operacional fica sem animação.
const CSS_CORRIDA = `
.ltc-mov { transition: left .2s cubic-bezier(.2,.8,.2,1), top .2s cubic-bezier(.2,.8,.2,1), transform .2s cubic-bezier(.2,.8,.2,1), opacity .15s ease; }
.ltc-rotulos { transition: opacity .2s ease; }
.ltc-valores { animation: ltc-valor .22s ease-out; }
.ltc-halo { animation: ltc-pulso 1.4s ease-out infinite; }
@keyframes ltc-valor { from { opacity: .25; transform: translateY(3px); } to { opacity: 1; transform: none; } }
@keyframes ltc-pulso { from { transform: scale(1); opacity: .5; } to { transform: scale(2.8); opacity: 0; } }
@media (prefers-reduced-motion: reduce) {
  .ltc-mov, .ltc-rotulos { transition: none; }
  .ltc-valores, .ltc-halo { animation: none; }
}`;

function GraficoCorrida({ dias, diasAnt, ate, nDias, nDiasAnt, aberto, hD, ab, abAnt, anoNum, mm, aAnt, mAnt, brl, mil, qtd }) {
  const W = 1000, H = 170, x0 = 44, base = 140, topo = 14;
  const [foco, setFoco] = useState(null); // { d, w, h }: dia sob o mouse e o tamanho do gráfico na tela
  const [ativo, setAtivo] = useState(false); // a dica some com fade, por isso o foco fica guardado
  const serie = (ds, n) => { let acc = 0; return Array.from({ length: n }, (_, i) => (acc += ds[i + 1]?.valor || 0)); };
  const atual = serie(dias, ate);
  const ant = Object.values(diasAnt).some(x => x.valor > 0) ? serie(diasAnt, nDiasAnt) : []; // mês anterior sem venda: sem linha
  const nX = Math.max(31, ate, nDiasAnt);
  const vmax = Math.max(1, ...atual, ...ant) * 1.08;
  const passoX = (W - x0 - 10) / (nX - 1);
  const cx = (d) => x0 + (d - 1) * passoX;
  const cy = (v) => base - v / vmax * (base - topo);
  const pts = (s) => s.map((v, i) => `${cx(i + 1).toFixed(1)},${cy(v).toFixed(1)}`).join(' ');
  const passo = vmax > 60000 ? 20000 : vmax > 25000 ? 10000 : 5000;
  const grade = [];
  for (let t = 0; t <= vmax; t += passo) grade.push(t);
  const dAnt = Math.min(ate, nDiasAnt);
  const marcas = [...new Set([1, 5, 10, 15, 20, 25, nX, ...(aberto ? [hD] : [])])].filter(d => d <= nX);

  // Mouse (ou dedo) em cima do gráfico → dia mais próximo da posição
  const focar = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    if (!r.width) return;
    const d = Math.min(nX, Math.max(1, Math.round(((e.clientX - r.left) / r.width * W - x0) / passoX) + 1));
    setFoco(f => (f && f.d === d && f.w === r.width && f.h === r.height ? f : { d, w: r.width, h: r.height }));
    setAtivo(true);
  };
  const soltar = (e) => { if (e.pointerType === 'mouse') setAtivo(false); };

  let sobre = null;
  if (foco) {
    const { d, w } = foco;
    const k = w / W; // o SVG mantém a proporção: a mesma escala vale para x e y
    const temAtual = d <= ate, temAnt = ant.length > 0 && d <= nDiasAnt;
    const vAtual = temAtual ? atual[d - 1] : 0, vAnt = temAnt ? ant[d - 1] : 0;
    const x = cx(d) * k;
    const ys = [temAtual && cy(vAtual), temAnt && cy(vAnt)].filter(y => y !== false);
    // pontos na metade de baixo → caixa no alto; pontos no alto → caixa embaixo
    const emBaixo = !ys.length || ys.reduce((s, y) => s + y, 0) / ys.length > (topo + base) / 2;
    const larg = Math.min(250, w - 8);
    const left = x + 14 + larg <= w - 4 ? x + 14 : Math.max(4, x - 14 - larg);
    const linhas = [
      {
        rot: ab, cor: '#4ade80', tracejado: false, valor: temAtual ? vAtual : null, doDia: dias[d],
        data: d <= nDias ? `${SEM[diaSemana(anoNum, mm, d)]} ${p2(d)}/${p2(mm)}` : '',
        falta: temAtual ? '' : d <= nDias ? 'ainda não chegou' : `${ab} não tem dia ${d}`,
      },
      ant.length > 0 && {
        rot: abAnt, cor: '#94a3b8', tracejado: true, valor: temAnt ? vAnt : null, doDia: diasAnt[d],
        data: d <= nDiasAnt ? `${SEM[diaSemana(aAnt, mAnt, d)]} ${p2(d)}/${p2(mAnt)}` : '',
        falta: temAnt ? '' : `${abAnt} não tem dia ${d}`,
      },
    ].filter(Boolean);
    const dif = vAtual - vAnt;
    const pct = vAnt > 0 ? Math.round(Math.abs(dif) / vAnt * 100) : null;
    const pontos = [temAtual && { y: cy(vAtual) * k, cor: '#15803d', r: 6 }, temAnt && { y: cy(vAnt) * k, cor: '#94a3b8', r: 5 }].filter(Boolean);
    sobre = (
      <>
        <div className="ltc-mov" style={{ position: 'absolute', left: x - 0.5, top: topo * k, height: (base - topo) * k, borderLeft: '1px dashed #64748b', opacity: ativo ? 1 : 0, pointerEvents: 'none' }} />
        {pontos.map((p, i) => (
          <div key={i} className="ltc-mov" style={{ position: 'absolute', left: x, top: p.y, opacity: ativo ? 1 : 0, pointerEvents: 'none' }}>
            <span className="ltc-halo" style={{ position: 'absolute', left: -p.r, top: -p.r, width: p.r * 2, height: p.r * 2, borderRadius: '50%', background: p.cor }} />
            <span style={{ position: 'absolute', left: -p.r, top: -p.r, width: p.r * 2, height: p.r * 2, borderRadius: '50%', background: p.cor, boxShadow: '0 0 0 2px #fff' }} />
          </div>
        ))}
        <div className="ltc-mov" style={{
          position: 'absolute', left, top: (emBaixo ? topo : base) * k, width: larg, zIndex: 2, pointerEvents: 'none', opacity: ativo ? 1 : 0,
          transform: `translateY(${emBaixo ? 0 : -100}%) scale(${ativo ? 1 : 0.96})`, transformOrigin: emBaixo ? '50% 0' : '50% 100%',
        }}>
          <div style={{ background: '#0a1628', color: '#fff', borderRadius: 8, padding: '8px 11px', boxShadow: '0 8px 22px rgba(10, 22, 40, 0.3)', fontSize: 11.5, fontVariantNumeric: 'tabular-nums' }}>
            <div key={d} className="ltc-valores">
              <div style={{ fontSize: 10, fontWeight: 800, color: '#94a3b8', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 5 }}>dia {d}</div>
              {linhas.map(l => (
                <div key={l.rot} style={{ marginBottom: 5 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ width: 14, flexShrink: 0, borderTop: `${l.tracejado ? '2px dashed' : '3px solid'} ${l.cor}` }} />
                    <span style={{ color: '#e2e8f0', fontWeight: 700 }}>{l.rot}</span>
                    {l.data && <span style={{ color: '#94a3b8', fontSize: 10.5 }}>{l.data}</span>}
                    <b style={{ marginLeft: 'auto', fontSize: 13 }}>{l.valor == null ? '—' : brl(l.valor)}</b>
                  </div>
                  <div style={{ color: '#94a3b8', fontSize: 10.5, textAlign: 'right' }}>
                    {l.falta || (l.doDia?.valor > 0 ? `+ ${brl(l.doDia.valor)} no dia · ${qtd(l.doDia.pedidos.size)}` : 'sem venda no dia')}
                  </div>
                </div>
              ))}
              {temAtual && temAnt && (
                <div style={{ borderTop: '1px solid rgba(148, 163, 184, 0.3)', paddingTop: 5, fontWeight: 700, color: dif > 0 ? '#4ade80' : dif < 0 ? '#f87171' : '#cbd5e1' }}>
                  {dif === 0 ? 'empatados' : `${ab} ${dif > 0 ? 'à frente' : 'atrás'}: ${dif > 0 ? '+' : '−'} ${brl(Math.abs(dif))}${pct != null ? ` (${dif > 0 ? '↑' : '↓'} ${pct}%)` : ''}`}
                </div>
              )}
              {!ant.length && <div style={{ color: '#94a3b8', fontSize: 10.5 }}>{abAnt} não teve venda no sistema</div>}
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div style={{ position: 'relative', touchAction: 'pan-y' }} onPointerMove={focar} onPointerDown={focar} onPointerLeave={soltar}>
        <style>{CSS_CORRIDA}</style>
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block' }}>
          {grade.map(t => (
            <g key={t}>
              <line x1={x0} x2={W - 10} y1={cy(t)} y2={cy(t)} stroke="#eef2f7" />
              <text x={x0 - 6} y={cy(t) + 3} fontSize="9" fill="#9ca3af" textAnchor="end">{t ? mil(t) : '0'}</text>
            </g>
          ))}
          {ant.length > 0 && <polyline points={pts(ant)} fill="none" stroke="#94a3b8" strokeWidth="2" strokeDasharray="5 4" />}
          {atual.length > 0 && <polyline points={pts(atual)} fill="none" stroke="#15803d" strokeWidth="3" />}
          <g className="ltc-rotulos" style={{ opacity: ativo ? 0.15 : 1 }}>
            {atual.length > 0 && (
              <>
                <circle cx={cx(ate)} cy={cy(atual[ate - 1])} r="4.5" fill="#15803d" />
                <text x={cx(ate) + (ate > nX - 6 ? -8 : 8)} y={cy(atual[ate - 1]) - 7} fontSize="11" fill="#15803d" fontWeight="800" textAnchor={ate > nX - 6 ? 'end' : 'start'}>{ab}: {brl(atual[ate - 1])}</text>
              </>
            )}
            {ant.length > 0 && aberto && (
              <>
                <circle cx={cx(dAnt)} cy={cy(ant[dAnt - 1])} r="4" fill="#94a3b8" />
                <text x={cx(dAnt) + 8} y={cy(ant[dAnt - 1]) + 15} fontSize="11" fill="#64748b" fontWeight="700">{abAnt} no dia {dAnt}: {brl(ant[dAnt - 1])}</text>
              </>
            )}
            {ant.length > 0 && (
              <text x={cx(nDiasAnt) - 2} y={cy(ant[nDiasAnt - 1]) - 7} fontSize="10" fill="#94a3b8" textAnchor="end">{abAnt} fechou em {brl(ant[nDiasAnt - 1])}</text>
            )}
          </g>
          {marcas.map(d => <text key={d} x={cx(d)} y={base + 14} fontSize="9.5" fill="#6b7280" textAnchor="middle">{d}</text>)}
        </svg>
        {sobre}
      </div>
      <div style={nota}>
        <span style={{ color: '#15803d', fontWeight: 800 }}>━━</span> {ab} &nbsp;&nbsp;
        {ant.length > 0 ? <><span style={{ color: '#94a3b8', fontWeight: 800 }}>╍╍</span> {abAnt}</> : <>{abAnt} não teve venda no sistema</>}
      </div>
    </>
  );
}

// ── Todos os meses ──────────────────────────────────────────────────────────
function VisaoTodos({ porDia, anoNum, mesesComVenda, chaveHoje, hA, hM, hD, brl, mil, qtd, setSel }) {
  const primeiro = mesesComVenda[0];
  const ultimo = anoNum === hA ? Math.max(hM, mesesComVenda[mesesComVenda.length - 1]) : mesesComVenda[mesesComVenda.length - 1];
  const meses = Array.from({ length: ultimo - primeiro + 1 }, (_, i) => primeiro + i);
  const fechados = meses.filter(mm => anoNum < hA || (anoNum === hA && mm < hM));
  const vmax = Math.max(1, ...meses.flatMap(mm => Object.values(porDia[chaveDe(anoNum, mm)] || {}).map(x => x.valor)));

  // Período do mês e dia da semana — só meses fechados
  const per = PERIODOS.map(() => ({ valor: 0, pedidos: new Set() }));
  const sem = SEM.map(() => ({ valor: 0, pedidos: new Set() }));
  const porMesPer = {};
  fechados.forEach(mm => {
    const ds = porDia[chaveDe(anoNum, mm)] || {};
    const pm = [0, 0, 0];
    Object.entries(ds).forEach(([d, x]) => {
      const i = periodoDe(+d);
      per[i].valor += x.valor; pm[i] += x.valor;
      x.pedidos.forEach(id => per[i].pedidos.add(id));
      const w = diaSemana(anoNum, mm, +d);
      sem[w].valor += x.valor; x.pedidos.forEach(id => sem[w].pedidos.add(id));
    });
    porMesPer[mm] = pm;
  });
  const totalFech = per.reduce((s, p) => s + p.valor, 0);
  const pct = (v) => (totalFech ? v / totalFech * 100 : 0);
  const aberto = meses.find(mm => chaveDe(anoNum, mm) === chaveHoje);
  if (aberto) {
    const ds = porDia[chaveHoje] || {};
    porMesPer[aberto] = [0, 0, 0];
    Object.entries(ds).forEach(([d, x]) => { porMesPer[aberto][periodoDe(+d)] += x.valor; });
  }

  // Frase-resumo
  let resumo = null;
  if (totalFech > 0) {
    const ordem = [0, 1, 2].sort((a, b) => per[b].valor - per[a].valor);
    const [forte, segundo, fraco] = ordem;
    const rotMeses = fechados.length === 1 ? MESES_AB[fechados[0] - 1].toLowerCase()
      : `${MESES_AB[fechados[0] - 1].toLowerCase()} a ${MESES_AB[fechados[fechados.length - 1] - 1].toLowerCase()}`;
    const r = (i) => Math.round(pct(per[i].valor));
    let frase;
    if (pct(per[forte].valor) - pct(per[segundo].valor) <= 3) {
      const [a, b] = [forte, segundo].sort((x, y) => x - y);
      frase = <><b>Nos meses fechados ({rotMeses}), o {PERIODOS[fraco].nome} do mês é o período mais fraco:</b> dias {PERIODOS[fraco].faixa} = {r(fraco)}% do faturamento. {maiuscula(PERIODOS[a].nome)} ({PERIODOS[a].faixa}) e {PERIODOS[b].nome} ({PERIODOS[b].faixa}) praticamente empatam: {r(a)}% e {r(b)}%.</>;
    } else {
      frase = <><b>Nos meses fechados ({rotMeses}), o {PERIODOS[forte].nome} do mês concentra mais vendas:</b> dias {PERIODOS[forte].faixa} = {r(forte)}% do faturamento. O período mais fraco é o {PERIODOS[fraco].nome} ({PERIODOS[fraco].faixa}), com {r(fraco)}%.</>;
    }
    const topSem = ORDEM_SEMANA.slice().sort((a, b) => sem[b].valor - sem[a].valor).slice(0, 2).sort((a, b) => ORDEM_SEMANA.indexOf(a) - ORDEM_SEMANA.indexOf(b));
    const fds = pct(sem[0].valor + sem[6].valor);
    resumo = <>{frase} {maiuscula(SEM_NOME[topSem[0]])} e {SEM_NOME[topSem[1]]} são os dias da semana mais fortes{fds < 5 ? '; sábado e domingo quase não têm venda.' : '.'}</>;
  }

  const top = meses.flatMap(mm => Object.entries(porDia[chaveDe(anoNum, mm)] || {}).map(([d, x]) => ({ mm, d: +d, valor: x.valor, n: x.pedidos.size })))
    .filter(x => x.valor > 0).sort((a, b) => b.valor - a.valor).slice(0, 5);
  const semMax = Math.max(1, ...sem.map(s => s.valor));
  const topSemVal = ORDEM_SEMANA.slice().sort((a, b) => sem[b].valor - sem[a].valor).slice(0, 2);
  const fraco = totalFech ? [0, 1, 2].sort((a, b) => per[a].valor - per[b].valor)[0] : -1;

  return (
    <>
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: '10px 14px', fontSize: 12.5, color: '#1e3a8a', lineHeight: 1.5 }}>
        💡 {resumo || <>Ainda não há mês fechado em {anoNum} para comparar os períodos — o mês em andamento aparece no mapa abaixo, mas fica fora das porcentagens.</>}
      </div>

      <div style={secao}>
        <div style={tituloSec}>🔥 Mapa de calor — valor vendido por dia <span style={nota}>· clique no mês para ver o dia a dia</span></div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 3, tableLayout: 'fixed', fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr>
                <th style={{ width: 70 }} />
                {Array.from({ length: 31 }, (_, i) => <th key={i} style={{ width: 24, fontSize: 9, color: '#6b7280', fontWeight: 600 }}>{i + 1}</th>)}
                <th style={{ width: 64 }} />
              </tr>
            </thead>
            <tbody>
              {meses.map(mm => {
                const chave = chaveDe(anoNum, mm), ds = porDia[chave] || {}, nD = ultimoDia(anoNum, mm), ehAberto = chave === chaveHoje;
                const maxMes = Math.max(0, ...Object.values(ds).map(x => x.valor));
                const tot = Object.values(ds).reduce((s, x) => s + x.valor, 0);
                return (
                  <tr key={mm}>
                    <td onClick={() => tot && setSel(mm)} style={{ fontSize: 11.5, fontWeight: 800, color: '#0a1628', whiteSpace: 'nowrap', cursor: tot ? 'pointer' : 'default' }}>
                      {MESES_AB[mm - 1]}{ehAberto && <span style={{ ...nota, marginLeft: 4 }}>(até {hD})</span>}
                    </td>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => {
                      if (d > nD) return <td key={d} />;
                      const x = ds[d], v = x?.valor || 0;
                      const futuro = ehAberto && d > hD;
                      const fundo = futuro ? 'repeating-linear-gradient(45deg,#f1f5f9,#f1f5f9 3px,#e2e8f0 3px,#e2e8f0 5px)'
                        : v ? `rgba(10, 22, 40, ${Math.min(1, 0.14 + Math.pow(v / vmax, 0.6) * 0.86).toFixed(2)})` : '#f3f4f6';
                      return (
                        <td key={d} style={{ padding: 0 }} title={futuro ? `${p2(d)}/${p2(mm)}: ainda não aconteceu` : `${SEM[diaSemana(anoNum, mm, d)]}, ${p2(d)}/${p2(mm)}: ${brl(v)}${v ? ` · ${qtd(x.pedidos.size)}` : ''}`}>
                          <div style={{ height: 22, borderRadius: 3, background: fundo, outline: v && v === maxMes ? '2px solid #f59e0b' : 'none', outlineOffset: -2 }} />
                        </td>
                      );
                    })}
                    <td style={{ fontSize: 11, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{mil(tot)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ ...nota, marginTop: 6, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
          menos {[0.14, 0.35, 0.6, 0.85, 1].map(a => <span key={a} style={{ display: 'inline-block', width: 16, height: 12, borderRadius: 2, background: `rgba(10, 22, 40, ${a})` }} />)} mais
          · <span style={{ display: 'inline-block', width: 14, height: 12, borderRadius: 2, outline: '2px solid #f59e0b', outlineOffset: -2, background: '#1e293b' }} /> melhor dia do mês
          {aberto && <> · listrado = dias de {MESES_NOME[aberto - 1]} que ainda não aconteceram</>}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 12 }}>
        <div style={secao}>
          <div style={tituloSec}>📊 Período do mês com mais vendas <span style={nota}>· meses fechados</span></div>
          {totalFech > 0 ? PERIODOS.map((p, i) => (
            <div key={p.rot} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, gap: 8 }}>
                <b style={{ color: '#0a1628' }}>{p.rot} · {p.faixa}</b>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{brl(per[i].valor)} · {qtd(per[i].pedidos.size)}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ height: 8, borderRadius: 3, background: i === fraco ? '#dc2626' : '#1d4ed8', width: `${pct(per[i].valor) * 0.75}%` }} />
                <span style={{ fontSize: 13, fontWeight: 800, color: i === fraco ? '#dc2626' : '#0a1628' }}>{Math.round(pct(per[i].valor))}%</span>
              </div>
            </div>
          )) : <div style={nota}>Sem mês fechado ainda.</div>}
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11.5, fontVariantNumeric: 'tabular-nums', marginTop: 4 }}>
            <tbody>
              <tr><td /><td style={{ textAlign: 'right', color: '#6b7280' }}>1–10</td><td style={{ textAlign: 'right', color: '#6b7280' }}>11–20</td><td style={{ textAlign: 'right', color: '#6b7280' }}>21–31</td></tr>
              {meses.filter(mm => porMesPer[mm]).map(mm => {
                const pm = porMesPer[mm], t = pm.reduce((s, v) => s + v, 0), parcial = mm === aberto;
                return (
                  <tr key={mm} style={{ borderTop: '1px solid #f1f5f9', color: parcial ? '#9ca3af' : '#374151' }}>
                    <td style={{ fontWeight: 700, padding: '2px 0' }}>{MESES_AB[mm - 1]}{parcial ? ' (parcial)' : ''}</td>
                    {pm.map((v, i) => <td key={i} style={{ textAlign: 'right' }}>{t ? Math.round(v / t * 100) : 0}%</td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={secao}>
          <div style={tituloSec}>📆 Dia da semana <span style={nota}>· meses fechados</span></div>
          {totalFech > 0 ? (
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 150, paddingTop: 26 }}>
              {ORDEM_SEMANA.map(w => (
                <div key={w} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: '#0a1628' }}>{Math.round(pct(sem[w].valor))}%</div>
                  <div style={{ height: Math.max(2, sem[w].valor / semMax * 80), background: topSemVal.includes(w) ? '#0a1628' : '#1d4ed8', borderRadius: '3px 3px 0 0', margin: '2px 4px 0' }} />
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#374151', marginTop: 3 }}>{SEM[w]}</div>
                  <div style={nota}>{qtd(sem[w].pedidos.size)}</div>
                </div>
              ))}
            </div>
          ) : <div style={nota}>Sem mês fechado ainda.</div>}
        </div>

        <div style={secao}>
          <div style={tituloSec}>🏆 Dias campeões</div>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 11.5, fontVariantNumeric: 'tabular-nums' }}>
            <tbody>
              {top.map((x, i) => (
                <tr key={`${x.mm}-${x.d}`} onClick={() => setSel(x.mm)} style={{ borderBottom: '1px solid #f1f5f9', cursor: 'pointer' }}>
                  <td style={{ padding: '3px 4px', fontWeight: 800, color: '#0a1628' }}>{i + 1}º</td>
                  <td style={{ padding: '3px 4px' }}>{p2(x.d)}/{p2(x.mm)}</td>
                  <td style={{ padding: '3px 4px', color: '#6b7280' }}>{SEM[diaSemana(anoNum, x.mm, x.d)]}</td>
                  <td style={{ padding: '3px 4px' }}>{qtd(x.n)}</td>
                  <td style={{ padding: '3px 4px', textAlign: 'right', fontWeight: 700 }}>{brl(x.valor)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {aberto && <div style={{ ...nota, marginTop: 8 }}>O mês em andamento aparece no mapa e nos campeões, mas fica fora das porcentagens para não distorcer (os dias que faltam ainda não aconteceram).</div>}
        </div>
      </div>
    </>
  );
}
